import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { AgendaService } from '@base/services/private/agenda.service';
import { Speaker } from '@base/models/responses/speaker.res';
import { ModalSpeakerComponent } from '@base/components/modal-speaker/modal-speaker.component';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { Sessions } from '@base/models/responses/sessions-of-company.res';
import { SearchReason } from '@base/models/others/match-reason';
import { GlobalService } from '@base/services/private/global.service';
import { JubliaAiService } from '@base/services/private/jublia-ai.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, finalize, Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-speaker-detail',
  templateUrl: './speaker-detail.component.html',
  styleUrl: './speaker-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpeakerDetailComponent implements AfterViewInit {
  @ViewChild('speakerDetailContainer', { read: ViewContainerRef }) speakerDetailContainer!: ViewContainerRef;
  @Input() descriptionMatchQuery: string | null = null;
  private readonly judySrv = inject(JubliaAiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cancelRequests$ = new Subject<void>();
  private descriptionReasons: SearchReason[] = [];
  private isDescriptionMatchLoading = false;
  private routeLeft = false;

  @Input() speakerId: string | null = null;
  @Input() searchReasons: SearchReason[] | null = null;
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly agendaSrv = inject(AgendaService);
  private readonly globalSrv = inject(GlobalService);
  speakerDetailInstance: ComponentRef<unknown> | undefined;
  speaker: Speaker | null = null;
  sessions: Sessions[] = [];

  constructor() {
    inject(Router)
      .events.pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.routeLeft = true;
        this.cancelRequests$.next();
      });
  }

  async ngAfterViewInit(): Promise<void> {
    await this.loadSpeakerDetailComponent();
    this.getSpeakerDetail();
  }

  /**
   * Fetches the details of the speaker.
   * @returns void
   */
  getSpeakerDetail(): void {
    const speakerId = this.speakerId;
    if (!speakerId || this.destroyRef.destroyed || this.routeLeft) return;
    this.cancelRequests$.next();
    this.descriptionReasons = [];
    this.speaker = null;
    const query = this.descriptionMatchQuery?.trim();
    if (query) this.getDescriptionMatches(speakerId, query);

    this.agendaSrv
      .getSpeakerDetail(speakerId)
      .pipe(take(1), takeUntil(this.cancelRequests$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (this.speakerId !== speakerId) return;
          if (!res?.speaker) {
            this.cancelRequests$.next();
            this.globalSrv.destroyModalRef('modal-speaker');
            return;
          }
          const reasons = this.searchReasons ?? res.speaker.search_reasons;
          this.speaker = {
            ...res.speaker,
            search_reasons: this.descriptionReasons.length ? [...(reasons ?? []), ...this.descriptionReasons] : reasons,
          };
          this.sessions = res.sessions;
          this.renderSpeakerDetail();
        },
        error: (err) => {
          console.error('Error fetching speaker detail:', err);
          this.cancelRequests$.next();
        },
      });
  }

  private getDescriptionMatches(speakerId: string, query: string): void {
    this.setDescriptionMatchLoading(true);
    this.judySrv
      .getDescriptionMatches({
        entity: 'speakers',
        search_query: query,
        description_match_ids: [speakerId],
      })
      .pipe(
        take(1),
        takeUntil(this.cancelRequests$),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.setDescriptionMatchLoading(false))
      )
      .subscribe({
        next: (res) => {
          if (this.speakerId !== speakerId) return;
          const phrases = res?.description_matches?.[speakerId];
          if (!Array.isArray(phrases)) return;
          this.descriptionReasons = phrases
            .filter((phrase): phrase is string => typeof phrase === 'string' && !!phrase.trim())
            .map((phrase) => ({ value: phrase.trim(), field_name: 'description', is_shown_in_listing: false }));
          if (!this.speaker || this.descriptionReasons.length === 0) return;
          this.speaker = {
            ...this.speaker,
            search_reasons: [...(this.speaker.search_reasons ?? []), ...this.descriptionReasons],
          };
          const modal = this.speakerDetailInstance?.instance as ModalSpeakerComponent | undefined;
          modal?.updateSearchReasons(this.speaker.search_reasons ?? []);
        },
        error: () => console.error('Failed to fetch speaker description matches.'),
      });
  }

  private setDescriptionMatchLoading(isLoading: boolean): void {
    this.isDescriptionMatchLoading = isLoading;
    const modal = this.speakerDetailInstance;
    if (!this.destroyRef.destroyed && modal && !modal.hostView?.destroyed) {
      modal.setInput('isDescriptionMatchLoading', isLoading);
    }
  }

  /**
   * Dynamically loads the speaker detail component into the view.
   */
  async loadSpeakerDetailComponent(): Promise<void> {
    try {
      const component = await this.dynamicImportLoaderSrv.loadComponent(
        this.speakerDetailContainer,
        WhiteLabel.COMPONENTS.ModalSpeaker // Replace with actual path
      );
      if (this.destroyRef.destroyed) return;
      if (component) {
        this.speakerDetailInstance = component.componentRef || undefined;
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  /**
   * Renders speaker details in the modal component.
   */
  renderSpeakerDetail(): void {
    const component = this.speakerDetailInstance?.instance as ModalSpeakerComponent; // Replace with actual component type
    if (component) {
      this.speakerDetailInstance?.setInput('speaker', this.speaker);
      this.speakerDetailInstance?.setInput('sessions', this.sessions);
      this.speakerDetailInstance?.setInput('isDescriptionMatchLoading', this.isDescriptionMatchLoading);
      this.speakerDetailInstance?.setInput('modalId', 'modal-speaker');
    }
  }
}
